import { useCallback, useEffect, useMemo, useState } from 'react';
import Clarity from '@microsoft/clarity';
import {
    addAuthentication,
    removeAuthentication,
} from '../localStorage/localStorageAuthenticationApi';
import { useApi, usePost } from '../httpClient/useAuthClient';

import { toastOnError } from '../utils/toastUtils';
import client from '../httpClient/client';
import { useDispatch } from 'react-redux';
import { createAuth, setCurrentUser, setLogin } from '../redux/authSlice';
import useTypedIntl from '../lang/utils/useTypedIntl';
import { USER_ROLE } from '../constants';
import { getInitialStudio, setStudio } from '../redux/studioSlice';
import { getLocalStorage } from '../localStorage/createPrefixedLocalStorage';
import { refreshTokenRoute } from '../routing/api-routes';
import {
    ApiOpFnAxiosResponse,
    ApiOpFnResponseData,
} from '../types/openApiHelpers';
import { v4 } from 'uuid';

export interface LoginPayload {
    username: string;
    password: string;
    loginRoute: string;
}

export interface WhoamiParamsInterface {
    set?: boolean;
    toastOnError?: boolean;
}

export type Whoami = ApiOpFnResponseData<'get_whoami'>;
export type WhoamiResponse = ApiOpFnAxiosResponse<'get_whoami'>;

const CLARITY_SESSION_STORAGE_KEY = 'clarity_custom_session_id';
let clarityInitialized = false;

const getOrCreateClaritySessionId = () => {
    if (typeof window === 'undefined') return undefined;
    try {
        const existingSessionId = window.sessionStorage.getItem(
            CLARITY_SESSION_STORAGE_KEY,
        );
        if (existingSessionId) return existingSessionId;
        const newSessionId = v4().toString();
        window.sessionStorage.setItem(
            CLARITY_SESSION_STORAGE_KEY,
            newSessionId,
        );
        return newSessionId;
    } catch {
        return undefined;
    }
};

const initClarityIfAllowed = (permessiAzioni: string[], userUuid: string) => {
    if (
        typeof window === 'undefined' ||
        clarityInitialized ||
        userUuid === undefined ||
        userUuid === null ||
        !permessiAzioni.includes('CAN_USE_CLARITY')
    ) {
        return;
    }

    try {
        // old v2: waz7ppbf64
        Clarity.init(process.env.REACT_APP_CLARITY_PROJECT_ID ?? 'wc5cbnt2of');
        try {
            Clarity.consentV2({
                ad_Storage: 'denied',
                analytics_Storage: 'granted',
            });
        } catch {
            Clarity.consent(true);
        }

        Clarity.identify(String(userUuid), getOrCreateClaritySessionId());
        clarityInitialized = true;
    } catch {
        // avoid blocking auth flow if clarity fails
    }
};

export function useLogin(key: string, shouldRefresh: boolean = false) {
    const { post } = usePost({ auth: false });
    const dispatch = useDispatch();
    const { f } = useTypedIntl();
    return useCallback(
        async ({ username, password, loginRoute }: LoginPayload) => {
            try {
                const prefixedStorage = getLocalStorage();
                const response = await post(loginRoute, { username, password });
                dispatch(
                    createAuth({ key: key, rawToken: response.data.token }),
                );
                if (shouldRefresh)
                    prefixedStorage.setItem(
                        `refresh${key}`,
                        JSON.stringify(response.data.refresh_token),
                    );
                return response;
            } catch (reason) {
                throw reason;
            }
        },
        [f, key, post, shouldRefresh],
    );
}

export function useRefresh(key: string) {
    const dispatch = useDispatch();

    const prefixedStorage = getLocalStorage();
    return useCallback(
        (extraPayloadData: any = {}) => {
            const refreshToken = prefixedStorage
                .getItem(`refresh${key}`)
                ?.slice(1, -1);
            if (!refreshToken)
                return new Promise(() => {
                    throw new Error('No token no party');
                });
            return client
                .post(
                    refreshTokenRoute,
                    JSON.stringify({
                        ...extraPayloadData,
                        refresh_token: refreshToken,
                    }),
                )
                .then((response) => {
                    addAuthentication(key, JSON.stringify(response.data.token));
                    prefixedStorage.setItem(
                        `refresh${key}`,
                        JSON.stringify(response.data['refresh_token']),
                    );

                    dispatch(
                        createAuth({ key: key, rawToken: response.data.token }),
                    );
                    return response;
                })
                .catch((reason) => {
                    throw reason;
                });
        },
        [localStorage],
    );
}

export const useWhoami = () => {
    const dispatch = useDispatch();
    const { call, loading, calledOnce } = useApi('get_whoami');
    const { f } = useTypedIntl();

    const [whoamiLoading, setWhoamiLoading] = useState<boolean | undefined>(
        loading,
    );

    const prefixedStorage = getLocalStorage();

    const whoami = useCallback(
        async (
            { set, toastOnError: toastOnErrorFlag }: WhoamiParamsInterface = {
                set: false,
                toastOnError: true,
            },
        ) => {
            const token = prefixedStorage.getItem('token');
            if (token === null) {
                setWhoamiLoading(false);
                dispatch(setLogin({ key: 'token', login: false }));
                setWhoamiLoading(false);
                throw new Error('NO TOKEN');
            } else {
                try {
                    const res = await call();
                    const userUuid = res?.data?.id;
                    const permessiAzioni = res.data?.permessiAzioni || [];
                    initClarityIfAllowed(permessiAzioni, userUuid);
                    // initAnalyticsIfAllowed(permessiAzioni, userUuid);
                    const prefixedStorage = getLocalStorage();
                    if (set) {
                        const whoamiResponse = res.data;
                        prefixedStorage.setItem(
                            'user-info',
                            JSON.stringify(whoamiResponse),
                        );
                        dispatch(
                            setCurrentUser({
                                currentUser: whoamiResponse,
                                key: 'token',
                            }),
                        );
                        dispatch(
                            setStudio({
                                studio: whoamiResponse.roles.includes(
                                    USER_ROLE.CONSULENTE_LEGALE,
                                )
                                    ? getInitialStudio(whoamiResponse)
                                    : null,
                                userId: whoamiResponse.id,
                            }),
                        );
                        setWhoamiLoading(true);
                    }
                    return res;
                } catch (reason) {
                    setWhoamiLoading(false);
                    dispatch(setLogin({ key: 'token', login: false }));
                    removeAuthentication('token');
                    prefixedStorage.removeItem('user-info');
                    prefixedStorage.removeItem('refreshtoken');
                    if (toastOnErrorFlag)
                        toastOnError(f({ id: 'system.invalidCredentials' }));
                    throw reason;
                }
            }
        },
        [f, call],
    );

    return useMemo(() => {
        return { loading: whoamiLoading, whoami, calledOnce };
    }, [whoamiLoading, whoami, calledOnce]);
};

export function useShareWhoami(tokenStorageName: string) {
    const dispatch = useDispatch();
    const { call, loading } = useApi('get_share_whoami', {
        authorization: {
            auth: true,
            tokenStorageName: tokenStorageName,
            shouldRefresh: false,
        },
    });
    const { f } = useTypedIntl();

    const [whoamiLoading, setWhoamiLoading] = useState<boolean | undefined>(
        loading,
    );

    useEffect(() => {
        setWhoamiLoading(loading);
    }, [loading]);

    const prefixedStorage = getLocalStorage();
    const token = prefixedStorage.getItem(tokenStorageName);

    const whoami = useCallback(
        async (
            { set, toastOnError: toastOnErrorFlag }: WhoamiParamsInterface = {
                set: false,
                toastOnError: true,
            },
        ) => {
            if (token === null) {
                dispatch(setLogin({ key: tokenStorageName, login: false }));
                setWhoamiLoading(false);
                throw new Error('NO TOKEN'); // <-- questo mantiene la tipizzazione
            } else {
                try {
                    const res = await call();
                    const prefixedStorage = getLocalStorage();
                    if (set) {
                        const whoamiResponse = res.data;
                        prefixedStorage.setItem(
                            'user-info',
                            JSON.stringify(whoamiResponse),
                        );
                        dispatch(
                            setCurrentUser({
                                currentUser: whoamiResponse,
                                key: tokenStorageName,
                            }),
                        );
                        dispatch(
                            setStudio({
                                studio: whoamiResponse.roles.includes(
                                    USER_ROLE.CONSULENTE_LEGALE,
                                )
                                    ? getInitialStudio(whoamiResponse)
                                    : null,
                                userId: whoamiResponse.id,
                            }),
                        );
                    }
                    return res;
                } catch (reason) {
                    dispatch(setLogin({ key: tokenStorageName, login: false }));
                    // todo probabilmente va pulito anche qua come nella chiamata whoami in caso decommentare
                    // removeAuthentication('token');
                    // prefixedStorage.removeItem('user-info');
                    // prefixedStorage.removeItem('refreshtoken');
                    if (toastOnErrorFlag)
                        toastOnError(f({ id: 'system.invalidCredentials' }));
                    throw reason;
                }
            }
        },
        [f, call],
    );

    return { loading: whoamiLoading, whoami };
}
